// apps/web/src/App.js (Rolling back to simpler, stable flow)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import './App.css';
import './theme.css';

import Sidebar from './components/Sidebar';
import BoardView from './components/BoardView';
import CollaboratorsPanel from './components/CollaboratorsPanel';
import TaskModal from './components/TaskModal';
import TemplateGalleryModal from './components/TemplateGalleryModal'; // NEW
import Marketplace from './components/Marketplace'; // NEW
import AdminDashboard from './components/AdminDashboard'; // NEW
import HelpCenter from './components/HelpCenter'; // NEW
import SettingsModal from './components/SettingsModal'; // NEW
import HealthIndicator from './components/HealthIndicator'; // HEALTH
import { useToast } from './components/Toast';

// --- Axios instance configured for our API ---
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1',
});

// Automatically attach Authorization header to all API requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Helper: attach Authorization header from localStorage token
function authHeaders() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Helper: get user-friendly error message with context
function getErrorMessage(err, context = 'action') {
  // Network errors
  if (!err.response) {
    if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
      return `Request timed out. Please check your connection and try again.`;
    }
    if (err.message?.includes('Network Error')) {
      return `Cannot connect to server. Please check your internet connection.`;
    }
    return `Network error. Please check your connection and try again.`;
  }

  // HTTP status codes with specific messages
  const status = err.response?.status;
  const serverMessage = err.response?.data?.message;

  switch (status) {
    case 400:
      return serverMessage || `Invalid request. Please check your input and try again.`;
    case 401:
      return `Session expired. Please log in again.`;
    case 403:
      return `You don't have permission to ${context}. Contact the workspace owner.`;
    case 404:
      return serverMessage || `Item not found. It may have been deleted.`;
    case 409:
      return serverMessage || `Conflict detected. The item may have been modified by someone else.`;
    case 413:
      return `File too large. Maximum size is 10 MB.`;
    case 422:
      return serverMessage || `Invalid data. Please check your input.`;
    case 429:
      return `Too many requests. Please wait a moment and try again.`;
    case 500:
      return `Server error. Our team has been notified. Please try again later.`;
    case 502:
    case 503:
    case 504:
      return `Server temporarily unavailable. Please try again in a moment.`;
    default:
      // Use server message if available, otherwise provide helpful fallback
      if (serverMessage) return serverMessage;
      if (err.message && !err.message.includes('Request failed')) {
        return err.message;
      }
      return `Unable to ${context}. Please try again or contact support if this persists.`;
  }
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // ===== AUTH STATE =====
  const [me, setMe] = useState(null); // { id, email, name, role, subscription_tier, ... }
  const [limits, setLimits] = useState(null); // { workspaces: { current, max }, lists_per_workspace }
  const [authLoading, setAuthLoading] = useState(false);

  // ===== WORKSPACE / LIST STATE =====
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');

  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const selectedListIdRef = useRef(null); // live reference for sockets/patch
  const [newListName, setNewListName] = useState('');

  // ===== TASK STATE =====
  const [tasks, setTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDesc, setNewTaskDesc] = useState('');

  // Task currently being edited in the modal
  const [editingTask, setEditingTask] = useState(null);

  // ===== LABELS STATE =====
  const [labels, setLabels] = useState([]);

  // ===== COLLABORATORS / INVITE STATE =====
  const [collaboratorsData, setCollaboratorsData] = useState({
    collaborators: [],
    pending_invites: [],
  });

  const [inviteEmails, setInviteEmails] = useState('');
  const [invitePermissionLevel, setInvitePermissionLevel] = useState('view'); // "view" | "edit"
  const [inviteTokenInput, setInviteTokenInput] = useState('');

  // ===== TEMPLATE GALLERY (UI) =====
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);

  // ===== SETTINGS MODAL =====
  const [showSettings, setShowSettings] = useState(false);

  // ===== MOBILE UI =====
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);

  // ===== DARK MODE & THEME =====
  const [darkMode, setDarkMode] = useState(() => {
    // Initialize from localStorage or default to false (light mode)
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });

  // Gradient presets (must match SettingsModal)
  const GRADIENT_PRESETS = {
    'professional-grey': 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
    'ocean-blue': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'sunset': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'forest': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'royal-purple': 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'golden-hour': 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
  };

  // Calculate luminance of a color to determine if it's light or dark
  function getLuminance(color) {
    // Handle hex colors
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16) / 255;
      const g = parseInt(hex.substr(2, 2), 16) / 255;
      const b = parseInt(hex.substr(4, 2), 16) / 255;

      // Apply gamma correction
      const [rg, gg, bg] = [r, g, b].map(c =>
        c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
      );

      // Calculate relative luminance
      return 0.2126 * rg + 0.7152 * gg + 0.0722 * bg;
    }

    // For gradients, extract first color
    if (color.includes('gradient')) {
      const match = color.match(/#[0-9a-fA-F]{6}/);
      if (match) {
        return getLuminance(match[0]);
      }
    }

    // Default to light background assumption
    return 0.5;
  }

  // Determine if background is light (true) or dark (false)
  function isLightBackground(color) {
    const luminance = getLuminance(color);
    return luminance > 0.5; // Threshold for light vs dark
  }

  // Apply text color theme based on background brightness
  function applyTextColorTheme(backgroundColor, isWallpaper = false) {
    // In dark mode, DON'T override text colors BUT still adjust surfaces for contrast
    if (darkMode) {
      // Only add text shadow for better readability
      if (isWallpaper) {
        document.documentElement.style.setProperty('--text-shadow', '0 1px 3px rgba(0,0,0,0.8), 0 2px 6px rgba(0,0,0,0.6)');
        // For wallpapers in dark mode, use semi-transparent dark surfaces
        document.documentElement.style.setProperty('--surface-primary', 'rgba(30, 41, 59, 0.9)');
        document.documentElement.style.setProperty('--surface-secondary', 'rgba(37, 47, 66, 0.9)');
        document.documentElement.style.setProperty('--surface-tertiary', 'rgba(15, 23, 42, 0.9)');
        document.documentElement.style.setProperty('--surface-active', 'rgba(71, 85, 105, 0.95)'); // Prominent for selection
      } else {
        document.documentElement.style.setProperty('--text-shadow', '0 1px 3px rgba(0,0,0,0.5)');

        // For solid/gradient in dark mode, ensure surfaces contrast with the background
        const isLight = isLightBackground(backgroundColor);
        if (isLight) {
          // Light custom background in dark mode → use lighter surfaces for cards to stand out
          document.documentElement.style.setProperty('--surface-primary', '#ffffff');
          document.documentElement.style.setProperty('--surface-secondary', '#f5f7fa');
          document.documentElement.style.setProperty('--surface-tertiary', '#e8edf3');
          document.documentElement.style.setProperty('--surface-active', '#d0e4f7'); // Light blue highlight
        } else {
          // Dark custom background in dark mode → use slightly lighter surfaces for contrast
          document.documentElement.style.setProperty('--surface-primary', '#334155'); // Lighter than #1a1a1a
          document.documentElement.style.setProperty('--surface-secondary', '#475569'); // Even lighter
          document.documentElement.style.setProperty('--surface-tertiary', '#1e293b');
          document.documentElement.style.setProperty('--surface-active', '#64748b'); // Much lighter for visibility
        }
      }
      return; // Keep dark mode text colors
    }

    // Only apply text colors in light mode with custom backgrounds
    if (isWallpaper) {
      // For wallpapers, always use light text with strong shadows for safety
      document.documentElement.style.setProperty('--text-primary', '#ffffff');
      document.documentElement.style.setProperty('--text-secondary', '#e2e8f0');
      document.documentElement.style.setProperty('--text-tertiary', '#cbd5e1');
      document.documentElement.style.setProperty('--text-muted', '#94a3b8');
      document.documentElement.style.setProperty('--text-inverse', '#0f172a');
      document.documentElement.style.setProperty('--text-shadow', '0 1px 3px rgba(0,0,0,0.8), 0 2px 6px rgba(0,0,0,0.6)');

      // Update surface colors for cards/buttons to be semi-transparent
      document.documentElement.style.setProperty('--surface-primary', 'rgba(30, 41, 59, 0.9)');
      document.documentElement.style.setProperty('--surface-secondary', 'rgba(37, 47, 66, 0.9)');
      document.documentElement.style.setProperty('--surface-tertiary', 'rgba(15, 23, 42, 0.9)');
      document.documentElement.style.setProperty('--surface-active', 'rgba(71, 85, 105, 0.95)'); // Prominent for selection
    } else {
      const isLight = isLightBackground(backgroundColor);

      if (isLight) {
        // Light background → Dark text and light surface colors
        document.documentElement.style.setProperty('--text-primary', '#1a2332');
        document.documentElement.style.setProperty('--text-secondary', '#4a5668');
        document.documentElement.style.setProperty('--text-tertiary', '#6b7585');
        document.documentElement.style.setProperty('--text-muted', '#8f9bb0');
        document.documentElement.style.setProperty('--text-inverse', '#f8fafc');
        document.documentElement.style.setProperty('--text-shadow', '0 1px 2px rgba(255,255,255,0.3)');

        // Light surface colors for cards/buttons
        document.documentElement.style.setProperty('--surface-primary', '#f5f7fa');
        document.documentElement.style.setProperty('--surface-secondary', '#dde3ea');
        document.documentElement.style.setProperty('--surface-tertiary', '#e8edf3');
        document.documentElement.style.setProperty('--surface-active', '#d0e4f7'); // Light blue highlight
      } else {
        // Dark background → Light text with semi-transparent light surfaces for contrast
        document.documentElement.style.setProperty('--text-primary', '#ffffff');
        document.documentElement.style.setProperty('--text-secondary', '#e2e8f0');
        document.documentElement.style.setProperty('--text-tertiary', '#cbd5e1');
        document.documentElement.style.setProperty('--text-muted', '#94a3b8');
        document.documentElement.style.setProperty('--text-inverse', '#0f172a');
        document.documentElement.style.setProperty('--text-shadow', '0 2px 4px rgba(0,0,0,0.8), 0 1px 2px rgba(0,0,0,0.6)');

        // Semi-transparent light surfaces for cards/buttons to stand out against dark gradient
        document.documentElement.style.setProperty('--surface-primary', 'rgba(255, 255, 255, 0.12)');
        document.documentElement.style.setProperty('--surface-secondary', 'rgba(255, 255, 255, 0.08)');
        document.documentElement.style.setProperty('--surface-tertiary', 'rgba(255, 255, 255, 0.05)');
        document.documentElement.style.setProperty('--surface-active', 'rgba(255, 255, 255, 0.2)'); // More opaque for visibility
      }
    }
  }

  // Apply background theme (solid, gradient, or wallpaper)
  function applyBackgroundTheme(theme) {
    const {
      backgroundType,
      solidColor,
      gradientPreset,
      gradientCustomStart,
      gradientCustomEnd,
      gradientDirection,
      wallpaperBase64,
      wallpaperBlur,
      wallpaperOverlay,
      wallpaperPosition,
      primaryColor
    } = theme;

    console.log('[App] Applying background theme:', { backgroundType, solidColor, gradientPreset, wallpaperBase64: wallpaperBase64 ? 'present' : 'none' });

    // Clear wallpaper-specific variables first
    document.documentElement.style.removeProperty('--bg-wallpaper-url');
    document.documentElement.style.removeProperty('--bg-wallpaper-blur');
    document.documentElement.style.removeProperty('--bg-wallpaper-overlay');
    document.documentElement.style.removeProperty('--bg-wallpaper-position');

    // Apply based on type
    if (backgroundType === 'solid') {
      if (solidColor) {
        console.log('[App] Setting solid color:', solidColor);
        // Set all background variables to the solid color
        document.documentElement.style.setProperty('--bg-app', solidColor);
        document.documentElement.style.setProperty('--bg-board', solidColor);
        document.documentElement.style.setProperty('--bg-sidebar', solidColor);
        document.documentElement.style.setProperty('--bg-panel', solidColor);
        // Adjust text colors for readability
        applyTextColorTheme(solidColor, false);
      } else {
        // Reset to default if empty
        document.documentElement.style.removeProperty('--bg-app');
        document.documentElement.style.removeProperty('--bg-board');
        document.documentElement.style.removeProperty('--bg-sidebar');
        document.documentElement.style.removeProperty('--bg-panel');
        document.documentElement.style.removeProperty('--text-shadow');
        // Reset text colors to let theme.css handle them
        document.documentElement.style.removeProperty('--text-primary');
        document.documentElement.style.removeProperty('--text-secondary');
        document.documentElement.style.removeProperty('--text-tertiary');
        document.documentElement.style.removeProperty('--text-muted');
        document.documentElement.style.removeProperty('--text-inverse');
        // Reset surface colors
        document.documentElement.style.removeProperty('--surface-primary');
        document.documentElement.style.removeProperty('--surface-secondary');
        document.documentElement.style.removeProperty('--surface-tertiary');
      }
    } else if (backgroundType === 'gradient') {
      let gradientValue = '';
      if (gradientPreset && GRADIENT_PRESETS[gradientPreset]) {
        gradientValue = GRADIENT_PRESETS[gradientPreset];
        console.log('[App] Setting gradient preset:', gradientPreset, gradientValue);
      } else {
        gradientValue = `linear-gradient(${gradientDirection}, ${gradientCustomStart} 0%, ${gradientCustomEnd} 100%)`;
        console.log('[App] Setting custom gradient:', gradientValue);
      }
      // Set all background variables to the gradient
      document.documentElement.style.setProperty('--bg-app', gradientValue);
      document.documentElement.style.setProperty('--bg-board', gradientValue);
      document.documentElement.style.setProperty('--bg-sidebar', gradientValue);
      document.documentElement.style.setProperty('--bg-panel', gradientValue);
      // Adjust text colors for readability (use first color from gradient)
      const firstColor = gradientPreset ? gradientValue : gradientCustomStart;
      applyTextColorTheme(firstColor, false);
    } else if (backgroundType === 'wallpaper') {
      if (wallpaperBase64) {
        console.log('[App] Setting wallpaper with blur:', wallpaperBlur, 'overlay:', wallpaperOverlay);
        // Set wallpaper to show through - make backgrounds transparent
        document.documentElement.style.setProperty('--bg-app', 'transparent');
        document.documentElement.style.setProperty('--bg-board', 'transparent');
        document.documentElement.style.setProperty('--bg-sidebar', 'rgba(0, 0, 0, 0.2)'); // Slight tint for differentiation
        document.documentElement.style.setProperty('--bg-panel', 'rgba(0, 0, 0, 0.2)'); // Slight tint for differentiation
        document.documentElement.style.setProperty('--bg-wallpaper-url', `url(${wallpaperBase64})`);
        document.documentElement.style.setProperty('--bg-wallpaper-blur', `${wallpaperBlur}px`);
        document.documentElement.style.setProperty('--bg-wallpaper-overlay', wallpaperOverlay);
        document.documentElement.style.setProperty('--bg-wallpaper-position', wallpaperPosition);
        // For wallpapers, always use light text with strong shadows
        applyTextColorTheme('', true);
      } else {
        // No wallpaper set, reset to default
        document.documentElement.style.removeProperty('--bg-app');
        document.documentElement.style.removeProperty('--bg-board');
        document.documentElement.style.removeProperty('--bg-sidebar');
        document.documentElement.style.removeProperty('--bg-panel');
        document.documentElement.style.removeProperty('--text-shadow');
        // Reset text colors to let theme.css handle them
        document.documentElement.style.removeProperty('--text-primary');
        document.documentElement.style.removeProperty('--text-secondary');
        document.documentElement.style.removeProperty('--text-tertiary');
        document.documentElement.style.removeProperty('--text-muted');
        document.documentElement.style.removeProperty('--text-inverse');
        // Reset surface colors
        document.documentElement.style.removeProperty('--surface-primary');
        document.documentElement.style.removeProperty('--surface-secondary');
        document.documentElement.style.removeProperty('--surface-tertiary');
      }
    }

    // Apply primary color
    if (primaryColor) {
      console.log('[App] Setting primary color:', primaryColor);
      document.documentElement.style.setProperty('--accent-primary', primaryColor);
      document.documentElement.style.setProperty('--accent-primary-hover', primaryColor);
    } else {
      // Reset to default if empty
      document.documentElement.style.removeProperty('--accent-primary');
      document.documentElement.style.removeProperty('--accent-primary-hover');
    }
  }

  // Load and apply user preferences
  useEffect(() => {
    async function loadPreferences() {
      console.log('[App] loadPreferences called', { me, hasToken: !!localStorage.getItem('accessToken') });

      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          console.log('[App] No token found, skipping preference load');
          return;
        }

        console.log('[App] Fetching preferences from API...');
        const res = await api.get('/preferences');
        const prefs = res.data?.preferences || {};
        console.log('[App] Preferences loaded:', prefs);

        // Apply theme from preferences
        const bgPrefs = prefs.preferences?.background || {};
        const primaryColor = prefs.preferences?.primaryColor || '';

        applyBackgroundTheme({
          backgroundType: bgPrefs.type || 'solid',
          solidColor: bgPrefs.solid?.color || '',
          gradientPreset: bgPrefs.gradient?.preset || '',
          gradientCustomStart: bgPrefs.gradient?.custom?.startColor || '#667eea',
          gradientCustomEnd: bgPrefs.gradient?.custom?.endColor || '#764ba2',
          gradientDirection: bgPrefs.gradient?.custom?.direction || '135deg',
          wallpaperBase64: bgPrefs.wallpaper?.base64 || '',
          wallpaperBlur: bgPrefs.wallpaper?.blur || 0,
          wallpaperOverlay: bgPrefs.wallpaper?.overlay || 0.3,
          wallpaperPosition: bgPrefs.wallpaper?.position || 'cover',
          primaryColor: primaryColor
        });

      } catch (err) {
        console.error('[App] Failed to load preferences:', err);
      }
    }

    loadPreferences();
  }, [me]); // Re-load when user changes

  // ===== KEYBOARD SHORTCUTS =====
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  // ===== REALTIME SOCKET =====
  const [socket, setSocket] = useState(null);
  const [activeSocketListId, setActiveSocketListId] = useState(null);

  useEffect(() => {
    selectedListIdRef.current = selectedListId;
  }, [selectedListId]);

  // Apply dark mode class to document element
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
    // Persist to localStorage
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  // Global keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e) {
      // Don't trigger shortcuts when typing in input/textarea/select
      const target = e.target;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';

      // ESC - Close modals/panels
      if (e.key === 'Escape') {
        if (editingTask) {
          setEditingTask(null);
        } else if (showTemplateGallery) {
          setShowTemplateGallery(false);
        } else if (showShortcutsHelp) {
          setShowShortcutsHelp(false);
        } else if (sidebarOpen) {
          setSidebarOpen(false);
        } else if (rightPanelOpen) {
          setRightPanelOpen(false);
        }
        return;
      }

      // Don't trigger other shortcuts when typing
      if (isTyping && e.key !== '?') return;

      // ? - Show keyboard shortcuts help
      if (e.key === '?' && e.shiftKey) {
        e.preventDefault();
        setShowShortcutsHelp(true);
        return;
      }

      // N - New task (when list is selected and no modals open)
      if (e.key === 'n' && !editingTask && !showTemplateGallery && !showShortcutsHelp && selectedListId) {
        e.preventDefault();
        // Focus the new task input
        const newTaskInput = document.querySelector('.NewTaskInput');
        if (newTaskInput) newTaskInput.focus();
        return;
      }

      // / - Focus search bar
      if (e.key === '/' && !editingTask && !showTemplateGallery && !showShortcutsHelp) {
        e.preventDefault();
        const searchInput = document.querySelector('.SearchInput');
        if (searchInput) searchInput.focus();
        return;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editingTask, showTemplateGallery, showShortcutsHelp, selectedListId, sidebarOpen, rightPanelOpen]);

  // --------------------------------------------------
  // AUTH FLOW
  // --------------------------------------------------

  async function handleTestLogin() {
    try {
      setAuthLoading(true);
      const res = await api.post('/dev/auth/login', {
        email: 'test@user.com',
        password: 'password123',
      });
      if (res.data && res.data.token) {
        localStorage.setItem('accessToken', res.data.token);
      }
      await loadMeAndEverything();
      toast.success('Logged in as test@user.com');
    } catch (err) {
      console.error('[Login failed]', err);
      toast.error(getErrorMessage(err, 'log in'));
    } finally {
      setAuthLoading(false);
    }
  }

  async function handleFriendLogin() {
    try {
      setAuthLoading(true);
      const res = await api.post('/dev/auth/login', {
        email: 'friend@example.com',
        password: 'password123',
      });
      if (res.data && res.data.token) {
        localStorage.setItem('accessToken', res.data.token);
      }
      await loadMeAndEverything();
      toast.success('Logged in as friend@example.com');
    } catch (err) {
      console.error('[Friend login failed]', err);
      toast.error(getErrorMessage(err, 'log in'));
    } finally {
      setAuthLoading(false);
    }
  }

  async function handleLogout() {
    localStorage.removeItem('accessToken');
    if (socket) socket.disconnect();
    setSocket(null);
    setActiveSocketListId(null);

    setMe(null);

    setWorkspaces([]);
    setSelectedWorkspaceId(null);

    setLists([]);
    setSelectedListId(null);

    setTasks([]);
    setEditingTask(null);

    setCollaboratorsData({ collaborators: [], pending_invites: [] });
  }

  async function loadMeAndEverything() {
    try {
      const profileRes = await api.get('/auth/me', { headers: authHeaders() });
      const userData = profileRes.data?.user || profileRes.data || null;
      const limitsData = profileRes.data?.limits || null;
      setMe(userData);
      setLimits(limitsData);
      await refreshWorkspaces();
    } catch (err) {
      console.warn('[loadMeAndEverything] failed:', err);
      setMe(null);
      setLimits(null);
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) loadMeAndEverything();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------------------------------------
  // COLLABORATORS / INVITES (Simple Function Definitions)
  // --------------------------------------------------

  const refreshCollaborators = useCallback(async (listId = selectedListId) => {
    if (!listId) return;
    try {
      const res = await api.get(`/invites/${listId}/collaborators`, { headers: authHeaders() });
      const data = res.data || { collaborators: [], pending_invites: [] };
      setCollaboratorsData(data);
    } catch (err) {
      console.error('[refreshCollaborators] error:', err);
      toast.error(getErrorMessage(err, 'load collaborators'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListId]);

  const refreshLabels = useCallback(async (listId = selectedListId) => {
    if (!listId) return;
    try {
      const res = await api.get(`/labels/${listId}`, { headers: authHeaders() });
      const data = res.data?.labels || [];
      setLabels(data);
    } catch (err) {
      console.error('[refreshLabels] error:', err);
    }
  }, [selectedListId]);

  async function sendInvites() {
    if (!selectedListId || !inviteEmails.trim()) {
      toast.warning('Please enter at least one email to invite.');
      return;
    }
    const emailList = inviteEmails.split(',').map((e) => e.trim()).filter(Boolean);
    try {
      await api.post(
        `/invites/${selectedListId}`,
        { emails: emailList, permission_level: invitePermissionLevel },
        { headers: authHeaders() }
      );
      toast.success('Invites sent successfully');
      setInviteEmails('');
      await refreshCollaborators();
    } catch (err) {
      console.error('[sendInvites] error:', err);
      toast.error(getErrorMessage(err, 'send invitations'));
    }
  }

  async function acceptInviteToken() {
    if (!inviteTokenInput.trim()) {
      toast.warning('Enter an invite token first.');
      return;
    }
    try {
      await api.post(`/invites/accept`, { token: inviteTokenInput.trim() }, { headers: authHeaders() });
      toast.success('Invite accepted successfully');
      setInviteTokenInput('');
      await refreshWorkspaces();
      if (selectedListId) await refreshCollaborators();
    } catch (err) {
      console.error('[acceptInviteToken] error:', err);
      toast.error(getErrorMessage(err, 'accept invitation'));
    }
  }

  async function revokeInvite(inviteId) {
    if (!window.confirm('Revoke this invite?')) return;
    try {
      await api.delete(`/invites/${inviteId}`, { headers: authHeaders() });
      toast.success('Invite revoked');
      await refreshCollaborators();
    } catch (err) {
      console.error('[revokeInvite] error:', err);
      toast.error(getErrorMessage(err, 'revoke invitation'));
    }
  }

  async function removeCollaborator(listId, userId) {
    if (!window.confirm('Remove this collaborator from the list?')) return;
    try {
      await api.delete(`/invites/${listId}/collaborators/${userId}`, { headers: authHeaders() });
      toast.success('Collaborator removed');
      await refreshCollaborators();
    } catch (err) {
      console.error('[removeCollaborator] error:', err);
      toast.error(getErrorMessage(err, 'remove collaborator'));
    }
  }


  // --------------------------------------------------
  // SOCKETS
  // --------------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!me || !token) return;

    const s = io(process.env.REACT_APP_WS_URL || 'http://localhost:3001', { auth: { token } });

    s.on('connect', () => console.log('[Socket] connected', s.id));
    s.on('connect_error', (err) => console.error('[Socket] connect_error:', err.message || err));

    // RESTORED: All socket listeners (Creation, Update, Deletion)
    s.on('task_created', ({ task }) => {
      console.log('[Socket] task_created received:', task?.id, 'for list:', task?.list_id, 'current list:', selectedListIdRef.current);
      if (!task) return;
      setTasks((prev) => {
        if (task.list_id !== selectedListIdRef.current) return prev;
        if (prev.find((t) => t.id === task.id)) return prev;
        return [...prev, task];
      });
    });

    s.on('task_updated', ({ task }) => {
      console.log('[Socket] task_updated received:', task?.id);
      if (!task) return;
      setTasks((prev) => {
        if (task.list_id !== selectedListIdRef.current) return prev;
        return prev.map((t) => (t.id === task.id ? task : t));
      });
    });

    s.on('task_deleted', ({ taskId }) => {
      if (!taskId) return;
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
    });

    s.on('collaborators_changed', ({ list_id }) => {
      if (list_id && list_id === selectedListIdRef.current) {
        // Refresh collaborators for the current list
        api.get(`/invites/${list_id}/collaborators`, { headers: authHeaders() })
          .then(res => {
            const data = res.data || { collaborators: [], pending_invites: [] };
            setCollaboratorsData(data);
          })
          .catch(err => console.error('[collaborators_changed] error:', err));
      }
    });

    // Label events
    s.on('label_created', ({ label }) => {
      console.log('[Socket] label_created received:', label?.id, 'for list:', label?.list_id);
      if (!label || label.list_id !== selectedListIdRef.current) return;
      setLabels((prev) => {
        if (prev.find((l) => l.id === label.id)) return prev;
        return [...prev, label];
      });
    });

    s.on('label_updated', ({ label }) => {
      console.log('[Socket] label_updated received:', label?.id);
      if (!label || label.list_id !== selectedListIdRef.current) return;
      setLabels((prev) => prev.map((l) => (l.id === label.id ? label : l)));
    });

    s.on('label_deleted', ({ labelId }) => {
      console.log('[Socket] label_deleted received:', labelId);
      if (!labelId) return;
      setLabels((prev) => prev.filter((l) => l.id !== labelId));
    });

    s.on('task_label_added', ({ taskId, labelId, label }) => {
      console.log('[Socket] task_label_added received:', 'task:', taskId, 'label:', label?.name);
      // Update the task in tasks array to reflect the label was added
      if (!taskId || !label) return;
      setTasks((prev) => prev.map((t) => {
        if (t.id !== taskId) return t;
        const taskLabels = t.labels || [];
        if (taskLabels.some((l) => l.id === labelId)) return t;
        return { ...t, labels: [...taskLabels, label] };
      }));
    });

    s.on('task_label_removed', ({ taskId, labelId }) => {
      console.log('[Socket] task_label_removed received:', 'task:', taskId, 'label:', labelId);
      // Update the task in tasks array to reflect the label was removed
      if (!taskId || !labelId) return;
      setTasks((prev) => prev.map((t) => {
        if (t.id !== taskId) return t;
        const taskLabels = t.labels || [];
        return { ...t, labels: taskLabels.filter((l) => l.id !== labelId) };
      }));
    });

    setSocket(s);
    return () => s.disconnect();
  }, [me]); // Only reconnect when user changes, not when other deps change

  useEffect(() => {
    if (!socket) return;
    if (activeSocketListId && activeSocketListId !== selectedListId) {
      console.log('[Socket] Leaving list room:', activeSocketListId);
      socket.emit('leave_list', activeSocketListId);
    }
    if (selectedListId && activeSocketListId !== selectedListId) {
      console.log('[Socket] Joining list room:', selectedListId);
      socket.emit('join_list', selectedListId);
      setActiveSocketListId(selectedListId);
    }
    if (!selectedListId && activeSocketListId) {
      console.log('[Socket] Leaving list room (no list selected):', activeSocketListId);
      socket.emit('leave_list', activeSocketListId);
      setActiveSocketListId(null);
    }
  }, [socket, selectedListId, activeSocketListId]);

  // --------------------------------------------------
  // WORKSPACES/LISTS
  // --------------------------------------------------
  async function refreshWorkspaces() {
    if (!localStorage.getItem('accessToken')) return;
    try {
      const res = await api.get('/workspaces', { headers: authHeaders() });
      const wsList = res.data?.workspaces || res.data || [];
      setWorkspaces(wsList);
    } catch (err) {
      console.error('[refreshWorkspaces] error:', err);
      toast.error(getErrorMessage(err, 'load workspaces'));
    }
  }

  async function createWorkspace() {
    if (!newWorkspaceName.trim()) return;
    try {
      await api.post('/workspaces', { name: newWorkspaceName.trim() }, { headers: authHeaders() });
      setNewWorkspaceName('');
      await refreshWorkspaces();
    } catch (err) {
      console.error('[createWorkspace] error:', err);
      toast.error(getErrorMessage(err, 'create workspace'));
    }
  }

  async function selectWorkspace(workspaceId) {
    setSelectedWorkspaceId(workspaceId);
    setSelectedListId(null);
    setTasks([]);
    setEditingTask(null);
    setCollaboratorsData({ collaborators: [], pending_invites: [] });
    setLabels([]);
    await refreshLists(workspaceId);
  }

  async function refreshLists(workspaceId) {
    if (!workspaceId) return;
    try {
      const res = await api.get(`/workspaces/${workspaceId}/lists`, { headers: authHeaders() });
      const listData = res.data?.lists || res.data || [];
      setLists(listData);
    } catch (err) {
      console.error('[refreshLists] error:', err);
      toast.error(getErrorMessage(err, 'load lists'));
    }
  }

  async function createList() {
    if (!selectedWorkspaceId || !newListName.trim()) return;
    try {
      await api.post('/lists', { workspace_id: selectedWorkspaceId, title: newListName.trim() }, { headers: authHeaders() });
      setNewListName('');
      await refreshLists(selectedWorkspaceId);
    } catch (err) {
      console.error('[createList] error:', err);
      toast.error(getErrorMessage(err, 'create list'));
    }
  }

  async function selectList(listId) {
    setSelectedListId(listId);
    setEditingTask(null);
    await refreshTasks(listId);
    await refreshCollaborators(listId);
    await refreshLabels(listId);
  }

  // --------------------------------------------------
  // TASKS
  // --------------------------------------------------
  async function refreshTasks(listId) {
    if (!listId) return;
    try {
      const res = await api.get(`/tasks/${listId}`, { headers: authHeaders() });
      const taskData = res.data?.tasks || res.data || [];
      setTasks(taskData);
    } catch (err) {
      console.error('[refreshTasks] error:', err);
      toast.error(getErrorMessage(err, 'load tasks'));
    }
  }

  async function createTask() {
    if (!selectedListId || !newTaskTitle.trim()) return;
    try {
      await api.post(
        `/tasks/${selectedListId}`,
        { title: newTaskTitle.trim(), description: newTaskDesc, status: 'todo' },
        { headers: authHeaders() }
      );
      setNewTaskTitle('');
      setNewTaskDesc('');
      await refreshTasks(selectedListId); // Manual refresh for stability
    } catch (err) {
      console.error('[createTask] error:', err);
      toast.error(getErrorMessage(err, 'create task'));
    }
  }

  async function updateTaskStatus(taskId, nextStatus) {
    try {
      // Optimistic update: change status immediately
      setTasks((prev) => prev.map((t) =>
        t.id === taskId ? { ...t, status: nextStatus } : t
      ));

      await api.patch(`/tasks/${taskId}`, { status: nextStatus }, { headers: authHeaders() });
      // Socket event will also trigger, confirming the update
    } catch (err) {
      console.error('[updateTaskStatus] error:', err);
      toast.error(getErrorMessage(err, 'update task'));
      // On error, reload tasks to restore state
      if (selectedListId) {
        const res = await api.get(`/tasks/${selectedListId}`, { headers: authHeaders() });
        setTasks(res.data?.tasks || []);
      }
    }
  }

  async function saveTaskEdits(taskId, fields) {
    try {
      // Optimistic update: apply changes immediately
      setTasks((prev) => prev.map((t) => {
        if (t.id === taskId) {
          return { ...t, ...fields };
        }
        return t;
      }));
      setEditingTask(null);

      await api.patch(`/tasks/${taskId}`, fields, { headers: authHeaders() });
      // Socket event will also trigger, confirming the update
    } catch (err) {
      console.error('[saveTaskEdits] error:', err);
      toast.error(getErrorMessage(err, 'save task'));
      // On error, reload tasks to restore state
      if (selectedListId) {
        const res = await api.get(`/tasks/${selectedListId}`, { headers: authHeaders() });
        setTasks(res.data?.tasks || []);
      }
    }
  }

  async function deleteTask(taskId) {
    try {
      // Optimistic update: remove from UI immediately
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      setEditingTask(null);

      await api.delete(`/tasks/${taskId}`, { headers: authHeaders() });
      // Socket event will also trigger, but optimistic update already done
    } catch (err) {
      console.error('[deleteTask] error:', err);
      toast.error(getErrorMessage(err, 'delete task'));
      // On error, reload tasks to restore state
      if (selectedListId) {
        const res = await api.get(`/tasks/${selectedListId}`, { headers: authHeaders() });
        setTasks(res.data?.tasks || []);
      }
    }
  }

  // ---- NEW: generic patch used by BoardView inline checklist (relies on task_updated socket) ----
  async function patchTask(taskId, fields) {
    try {
      // Optimistic update: apply changes immediately (critical for inline checklist editing)
      setTasks((prev) => prev.map((t) => {
        if (t.id === taskId) {
          return { ...t, ...fields };
        }
        return t;
      }));

      await api.patch(`/tasks/${taskId}`, fields, { headers: authHeaders() });
      // Socket event will also trigger, confirming the update
    } catch (err) {
      console.error('[patchTask] error:', err);
      toast.error(getErrorMessage(err, 'update task'));
      // On error, reload tasks to restore state
      if (selectedListId) {
        const res = await api.get(`/tasks/${selectedListId}`, { headers: authHeaders() });
        setTasks(res.data?.tasks || []);
      }
    }
  }

  // ---- Reorder task (drag-and-drop) ----
  async function reorderTask(taskId, newPosition, newStatus) {
    try {
      // Optimistic update: immediately update task position and status
      setTasks((prev) => prev.map((t) => {
        if (t.id === taskId) {
          return { ...t, position: newPosition, status: newStatus };
        }
        return t;
      }));

      await api.patch(`/tasks/${taskId}/position`, { position: newPosition, status: newStatus }, { headers: authHeaders() });
      // Socket event will confirm the update
    } catch (err) {
      console.error('[reorderTask] error:', err);
      toast.error(getErrorMessage(err, 'reorder task'));
      // On error, reload tasks to restore state
      if (selectedListId) {
        const res = await api.get(`/tasks/${selectedListId}`, { headers: authHeaders() });
        setTasks(res.data?.tasks || []);
      }
    }
  }

  // --------------------------------------------------
  // DELETE HELPERS (Simple Function Definitions)
  // --------------------------------------------------

  async function deleteSelectedList(listId = selectedListId) {
    if (!listId) return toast.warning('Select a list first.');
    try {
      await api.delete(`/lists/${listId}`, { headers: authHeaders() });
      // If deleting the currently selected list, clear selection
      if (listId === selectedListId) {
        setSelectedListId(null);
        setTasks([]);
        setCollaboratorsData({ collaborators: [], pending_invites: [] });
      }
      setLists((prev) => prev.filter((l) => l.id !== listId));
      if (selectedWorkspaceId) await refreshLists(selectedWorkspaceId);
      toast.success('List deleted successfully');
    } catch (err) {
      toast.error(getErrorMessage(err, 'delete list'));
    }
  }

  async function deleteSelectedWorkspace(workspaceId = selectedWorkspaceId) {
    if (!workspaceId) return toast.warning('Select a workspace first.');
    try {
      await api.delete(`/workspaces/${workspaceId}`, { headers: authHeaders() });
      // If deleting the currently selected workspace, clear all selections
      if (workspaceId === selectedWorkspaceId) {
        setSelectedWorkspaceId(null);
        setSelectedListId(null);
        setLists([]);
        setTasks([]);
        setCollaboratorsData({ collaborators: [], pending_invites: [] });
      }
      setWorkspaces((prev) => prev.filter((w) => w.id !== workspaceId));
      await refreshWorkspaces();
      toast.success('Workspace deleted successfully');
    } catch (err) {
      toast.error(getErrorMessage(err, 'delete workspace'));
    }
  }

  // --------------------------------------------------
  // RENAME HELPERS
  // --------------------------------------------------

  async function renameWorkspace(workspaceId, newName) {
    if (!newName?.trim()) return toast.warning('Workspace name cannot be empty.');
    try {
      await api.patch(`/workspaces/${workspaceId}`, { name: newName.trim() }, { headers: authHeaders() });
      // Update local state
      setWorkspaces((prev) =>
        prev.map((w) => (w.id === workspaceId ? { ...w, name: newName.trim() } : w))
      );
    } catch (err) {
      toast.error(getErrorMessage(err, 'rename workspace'));
    }
  }

  async function renameList(listId, newTitle) {
    if (!newTitle?.trim()) return toast.warning('List title cannot be empty.');
    try {
      await api.patch(`/lists/${listId}`, { title: newTitle.trim() }, { headers: authHeaders() });
      // Update local state
      setLists((prev) =>
        prev.map((l) => (l.id === listId ? { ...l, title: newTitle.trim() } : l))
      );
    } catch (err) {
      toast.error(getErrorMessage(err, 'rename list'));
    }
  }


  // --------------------------------------------------
  // TEMPLATE APPLICATION
  // --------------------------------------------------
  async function handleApplyTemplate(template) {
    if (!selectedListId) {
      toast.warning('Pick a list first, then choose a template.');
      return;
    }

    let title = template?.title || 'New Task';
    // Use template status if provided. Default to 'todo'.
    let status = template?.status || 'todo'; 
    let description = '';

    // Interactive Checklist regression fix: Use full description for creation
    if (template?.id === 'interactive-checklist') {
      description = `### Checklist`;
      title = 'New checklist';
    } else if (template?.id === 'meeting-agenda') {
      description = [
        '### Agenda',
        '- [ ] Topic 1',
        '- [ ] Topic 2',
        '',
        '### Notes',
        '(add notes here…)'
      ].join('\n');
    } else {
      // For all other V2 templates, use the content from the file.
      description = template.description || template.summary || template.html || '';
    }

    try {
      await api.post(`/tasks/${selectedListId}`, { title, description, status }, { headers: authHeaders() });
      // Stability fix: Manual refresh is needed here due to the persistent status timing issue.
      await refreshTasks(selectedListId);
      
    } catch (err) {
      toast.error(getErrorMessage(err, 'create task from template'));
    } finally {
      setShowTemplateGallery(false);
    }
  }

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  const selectedWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId);
  const selectedList = lists.find((l) => l.id === selectedListId);

  return (
    <div className="AppShell">
      <header className="TopBar">
        <div className="TopBar-left">
          {/* Mobile menu button - left sidebar */}
          <button
            className="Btn MobileMenuBtn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
            title="Toggle sidebar"
          >
            ☰
          </button>

          <div className="Brand">
            <span className="Brand-main">Sentia Tasks</span>
            <span className="Brand-env">(Beta)</span>
          </div>

          {/* Marketplace Link */}
          <button
            className="Btn"
            onClick={() => navigate(location.pathname === '/marketplace' ? '/' : '/marketplace')}
            style={{ marginLeft: 16 }}
          >
            {location.pathname === '/marketplace' ? '← Back to App' : '🛒 Marketplace'}
          </button>

          {/* Help Centre Link */}
          <button
            className="Btn"
            onClick={() => navigate(location.pathname === '/help' ? '/' : '/help')}
            style={{ marginLeft: 8 }}
          >
            {location.pathname === '/help' ? '← Back to App' : '❓ Help'}
          </button>

          {/* Settings Button */}
          <button
            className="Btn"
            onClick={() => setShowSettings(true)}
            style={{ marginLeft: 8 }}
            title="App Settings"
          >
            ⚙️ Settings
          </button>

          {/* Admin Link (admins and owners only) */}
          {(() => {
            const showAdminButton = me && (me.role === 'admin' || me.role === 'owner');
            console.log('[App] Admin button check - me:', me, 'role:', me?.role, 'showButton:', showAdminButton);
            return showAdminButton ? (
              <button
                className="Btn"
                onClick={() => navigate(location.pathname === '/admin' ? '/' : '/admin')}
                style={{ marginLeft: 8 }}
              >
                {location.pathname === '/admin' ? '← Back to App' : '⚙️ Admin'}
              </button>
            ) : null;
          })()}
        </div>

        <div className="TopBar-right">
          {!me && (
            <>
              <button
                className="Btn primary"
                onClick={() => window.location.href = 'https://sentia-app.com/login?redirect=' + encodeURIComponent(window.location.origin)}
              >
                Login
              </button>
              <button
                className="Btn"
                onClick={() => window.location.href = 'https://sentia-app.com/signup?redirect=' + encodeURIComponent(window.location.origin)}
                style={{ marginLeft: 8 }}
              >
                Sign Up
              </button>
            </>
          )}
          {me && <button className="Btn" onClick={handleLogout}>Logout</button>}

          {/* Dark mode toggle */}
          <button
            className="ThemeToggle"
            onClick={() => setDarkMode(!darkMode)}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <span className="ThemeToggle-icon">{darkMode ? '☀️' : '🌙'}</span>
            {darkMode ? 'Light' : 'Dark'}
          </button>

          {/* Keyboard shortcuts button */}
          <button
            className="Btn"
            onClick={() => setShowShortcutsHelp(true)}
            aria-label="Show keyboard shortcuts"
            title="Keyboard shortcuts (?)"
            style={{ fontSize: 18 }}
          >
            ⌨️
          </button>

          {/* Mobile menu button - right panel */}
          <button
            className="Btn MobileMenuBtn"
            onClick={() => setRightPanelOpen(!rightPanelOpen)}
            aria-label="Toggle collaborators"
            title="Toggle collaborators"
          >
            👥
          </button>
        </div>
      </header>

      <Routes>
        {/* Marketplace Route */}
        <Route path="/marketplace" element={<Marketplace />} />

        {/* Admin Dashboard Route (includes Audit Logs tab) */}
        <Route path="/admin" element={<AdminDashboard user={me} />} />

        {/* Help Centre Route */}
        <Route path="/help" element={<HelpCenter />} />

        {/* Main App Route */}
        <Route path="/" element={
          <>
      <div className="MainLayout">
        {/* Mobile backdrop for sidebar */}
        {sidebarOpen && (
          <div
            className="MobileBackdrop"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          />
        )}

        <div className={`SidebarWrapper ${sidebarOpen ? 'mobile-open' : ''}`}>
          <Sidebar
            workspaces={workspaces}
            selectedWorkspaceId={selectedWorkspaceId}
            onSelectWorkspace={(wid) => { selectWorkspace(wid); setSidebarOpen(false); }}
            newWorkspaceName={newWorkspaceName}
            setNewWorkspaceName={setNewWorkspaceName}
            onCreateWorkspace={createWorkspace}
            lists={lists}
            selectedListId={selectedListId}
            onSelectList={(lid) => { selectList(lid); setSidebarOpen(false); }}
            newListName={newListName}
            setNewListName={setNewListName}
            onCreateList={createList}
            onDeleteWorkspace={deleteSelectedWorkspace}
            onDeleteList={deleteSelectedList}
            onRenameWorkspace={renameWorkspace}
            onRenameList={renameList}
            isAuthed={!!me}
            user={me}
            limits={limits}
          />
        </div>

        <BoardView
          isAuthed={!!me}
          workspace={selectedWorkspace}
          list={selectedList}
          tasks={tasks}
          collaborators={collaboratorsData?.collaborators || []}
          labels={labels}
          newTaskTitle={newTaskTitle}
          setNewTaskTitle={setNewTaskTitle}
          newTaskDesc={newTaskDesc}
          setNewTaskDesc={setNewTaskDesc}
          onCreateTask={createTask}
          onChangeStatus={updateTaskStatus}
          onOpenTask={(task) => setEditingTask(task)}
          onOpenTemplates={() => setShowTemplateGallery(true)}
          onPatchTask={patchTask}
          onReorderTask={reorderTask}
        />

        {/* Mobile backdrop for right panel */}
        {rightPanelOpen && (
          <div
            className="MobileBackdrop"
            onClick={() => setRightPanelOpen(false)}
            aria-label="Close collaborators panel"
          />
        )}

        <div className={`RightPanelWrapper ${rightPanelOpen ? 'mobile-open' : ''}`}>
          <CollaboratorsPanel
            list={selectedList}
            collaboratorsData={collaboratorsData}
            inviteEmails={inviteEmails}
            setInviteEmails={setInviteEmails}
            invitePermissionLevel={invitePermissionLevel}
            setInvitePermissionLevel={setInvitePermissionLevel}
            onSendInvites={sendInvites}
            inviteTokenInput={inviteTokenInput}
            setInviteTokenInput={setInviteTokenInput}
            onAcceptInvite={acceptInviteToken}
            onRefreshCollaborators={refreshCollaborators}
            onRevokeInvite={revokeInvite}
            onRemoveCollaborator={removeCollaborator}
            currentUserId={me?.id}
            isAuthed={!!me}
          />
        </div>
      </div>

      {editingTask && (
        <TaskModal
          task={editingTask}
          collaborators={collaboratorsData?.collaborators || []}
          labels={labels}
          listId={selectedListId}
          workspaces={workspaces}
          onClose={() => setEditingTask(null)}
          onSave={async (updates) => { await saveTaskEdits(editingTask.id, updates); }}
          onDelete={async () => { await deleteTask(editingTask.id); }}
          onRefreshLabels={refreshLabels}
        />
      )}

      {showTemplateGallery && (
        <TemplateGalleryModal
          open={showTemplateGallery}
          onClose={() => setShowTemplateGallery(false)}
          listId={selectedListId}
          onTasksCreated={(tasks) => {
            // Refresh tasks after template is applied
            if (selectedListId) {
              refreshTasks(selectedListId);
            }
            setShowTemplateGallery(false);
          }}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          onThemeChange={(themeData) => {
            console.log('[App] onThemeChange called with:', themeData);
            applyBackgroundTheme(themeData);
          }}
        />
      )}

      {/* Keyboard Shortcuts Help Modal */}
      {showShortcutsHelp && (
        <div className="ModalOverlay" onClick={() => setShowShortcutsHelp(false)}>
          <div className="TaskEditPanel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="TaskEditHeader">
              <div className="TaskEditHeaderLeft">
                <h2 className="TaskEditHeading">⌨️ Keyboard Shortcuts</h2>
                <p className="TaskEditSub">Boost your productivity</p>
              </div>
              <div className="TaskEditHeaderRight">
                <button className="Btn" onClick={() => setShowShortcutsHelp(false)} title="Close">×</button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Navigation</h3>
                <table style={{ width: '100%', fontSize: 13, color: 'var(--text-secondary)' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '8px 0', fontFamily: 'monospace', color: 'var(--text-primary)' }}>ESC</td>
                      <td style={{ padding: '8px 0' }}>Close open modal or panel</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '8px 0', fontFamily: 'monospace', color: 'var(--text-primary)' }}>?</td>
                      <td style={{ padding: '8px 0' }}>Show this help dialog</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '8px 0', fontFamily: 'monospace', color: 'var(--text-primary)' }}>/</td>
                      <td style={{ padding: '8px 0' }}>Focus search bar</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Actions</h3>
                <table style={{ width: '100%', fontSize: 13, color: 'var(--text-secondary)' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '8px 0', fontFamily: 'monospace', color: 'var(--text-primary)' }}>N</td>
                      <td style={{ padding: '8px 0' }}>New task (when list selected)</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '8px 0', fontFamily: 'monospace', color: 'var(--text-primary)' }}>Enter</td>
                      <td style={{ padding: '8px 0' }}>Submit input (varies by context)</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '8px 0', fontFamily: 'monospace', color: 'var(--text-primary)' }}>Ctrl+Enter</td>
                      <td style={{ padding: '8px 0' }}>Submit textarea (comments, invites)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Editing</h3>
                <table style={{ width: '100%', fontSize: 13, color: 'var(--text-secondary)' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '8px 0', fontFamily: 'monospace', color: 'var(--text-primary)' }}>ESC</td>
                      <td style={{ padding: '8px 0' }}>Cancel inline edit</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', fontFamily: 'monospace', color: 'var(--text-primary)' }}>Enter</td>
                      <td style={{ padding: '8px 0' }}>Save inline edit (workspace/list names, labels)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 8, padding: 12, backgroundColor: 'var(--surface-tertiary)', borderRadius: 8, border: '1px solid var(--border-light)' }}>
                <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: 0 }}>
                  💡 <strong>Tip:</strong> Most shortcuts only work when you're not typing in an input field.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copyright Footer */}
      <footer className="AppFooter">
        © 2025 Sentia App. All Rights Reserved.
      </footer>

      {/* Health Indicator */}
      <HealthIndicator position="bottom-right" expanded={false} />
          </>
        } />
      </Routes>
    </div>
  );
}